import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Alert,
  Badge,
  Button,
  Form,
  InputGroup,
  Modal,
  Table,
} from 'react-bootstrap';
import OutletHead from '../../../../components/common/outlet-head';
import {
  query,
  collection,
  onSnapshot,
  updateDoc,
  doc,
  addDoc,
  getCountFromServer,
  setDoc,
} from 'firebase/firestore';
import { db } from './../../../../firebase';
import {
  IoAdd,
  IoArrowBackOutline,
  IoDocumentTextOutline,
  IoRemove,
} from 'react-icons/io5';
import { useAuth } from '../../../../context/AuthContext';
import { format } from 'date-fns';
import Skeleton from 'react-loading-skeleton';
import { CgAlignBottom } from 'react-icons/cg';
import { InvoiceNumber } from 'invoice-number';
import { toast } from 'react-toastify';

import {
  getInvoiceAmounts,
  invoiceOutstanding,
  getTotalsForInvoices,
  fmt,
  round2,
} from '../../../../components/common/utils/outstanding';

const safeNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};

const Invoices = () => {
  const { user, businessProfile } = useAuth();
  const { bid } = user.claims;
  const location = useLocation();
  const navigate = useNavigate();

  const [isLoading, setIsLoading] = useState(true);
  const [item] = useState(location.state.items || null);
  const [invoices, setInvoices] = useState(null);
  const [paymentPlans] = useState(
    (location.state && location.state.paymentPlans) || null
  );
  const [receiptID, setReceiptID] = useState(null);
  const [invoiceData, setInvoiceData] = useState({ paymentMode: 'Cash' });
  const [inputError, setInputError] = useState(null);
  const [receivePaymentModal, setReceivePaymentModal] = useState(false);

  // ---------- RECEIPT ID ----------
  async function getNewReceiptID() {
    const q = query(collection(db, 'Businesses', bid, 'Receipts'));
    const snapshot = await getCountFromServer(q);
    if (snapshot) {
      const d = new Date();
      const year = d.getFullYear();
      let month = d.getMonth() + 1;
      month = month.toString().length === 1 ? `0${month}` : month;
      let reptID = `REPT${year}${month}-${(
        '000' + snapshot.data().count
      ).slice(-4)}`;
      reptID = InvoiceNumber.next(reptID);
      setReceiptID(reptID);
    }
  }

  // ---------- LOAD INVOICES ----------
  function getInvoices(bookingID) {
    const q = query(
      collection(db, 'Businesses', bid, 'Bookings', bookingID, 'Invoices')
    );
    const unsubscribe = onSnapshot(
      q,
      (querySnapshot) => {
        const tempData = [];
        querySnapshot.forEach((snap) => {
          tempData.push({ id: snap.id, ...snap.data() });
        });
        setInvoices(tempData);
        setTimeout(() => setIsLoading(false), 500);
      },
      (error) => {
        console.log(error.message);
        setIsLoading(false);
      }
    );

    return () => unsubscribe();
  }

  useEffect(() => {
    if (location.state && location.state.items && location.state.items.id) {
      getNewReceiptID();
      getInvoices(location.state.items.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location]);

  // ---------- SAVE RECEIPT (LEGACY DIRECT-INVOICE PAYMENT) ----------
  async function handleSaveReceipt(bookingID, id, updatedData) {
    // legacy path – keep for now, but note: main flow moved to Receipts module
    delete updatedData.updatedOn;
    delete updatedData.id;
    updatedData = { ...updatedData, updatedOn: new Date() };

    const tempAmountReceived =
      safeNum(updatedData.paidAmount) + safeNum(updatedData.amountReceived);

    const { partyDetails, taxRate } = location.state.items;

    try {
      await updateDoc(
        doc(db, 'Businesses', bid, 'Bookings', bookingID, 'Invoices', id),
        {
          // NOTE: This is legacy; in new model we rely on principal/tax/overdue* fields
          amountReceived: tempAmountReceived,
          paymentDate: updatedData.paymentDate,
          paymentMode: updatedData.paymentMode,
          updatedOn: new Date(),
        }
      );

      const receiptData = {
        createdOn: new Date(),
        date: updatedData && updatedData.paymentDate,
        description: updatedData.paymentRemark ? updatedData.paymentRemark : '',
        amountReceived: updatedData.amountReceived,
        // legacy invoiceIDs – new flow uses allocations from Receipts
        invoiceIDs: [{ id, amountReceived: updatedData.amountReceived }],
        partyID: partyDetails.id,
        bookingID,
        paymentDetails: {
          paymentName:
            (updatedData && updatedData.paymentName) || '',
          chequeDate:
            (updatedData && updatedData.chequeDate) || null,
          dueDate: (updatedData && updatedData.dueDate) || null,
          paymentMode:
            (updatedData && updatedData.paymentMode) || '',
          paymentReceivedFrom:
            (updatedData && updatedData.paymentReceivedFrom) || '',
          paymentReferenceNumber:
            (updatedData && updatedData.paymentReferenceNumber) || '',
        },
      };

      await setDoc(
        doc(db, 'Businesses', bid, 'Receipts', receiptID),
        receiptData
      );

      const narration =
        paymentPlans && paymentPlans.find((x) => x.id === updatedData.planID)
          ? paymentPlans.find((x) => x.id === updatedData.planID).name
          : '';

      const ledgerData = {
        createdOn: new Date(),
        transactionDate: updatedData && updatedData.paymentDate,
        description: updatedData.paymentRemark ? updatedData.paymentRemark : '',
        narration: [narration],
        accountCode: partyDetails.partyType ? partyDetails.partyType : null,
        debitAmount: updatedData.amountReceived,
        creditAmount: null,
        receiptID,
        invoiceIDs: [{ id, amountReceived: updatedData.amountReceived }],
        partyID: partyDetails.id,
        bookingID,
        paymentDetails: {
          chequeDate:
            (updatedData && updatedData.chequeDate) || null,
          dueDate: (updatedData && updatedData.dueDate) || null,
          paymentMode:
            (updatedData && updatedData.paymentMode) || '',
          paymentReceivedFrom:
            (updatedData && updatedData.paymentReceivedFrom) || '',
          paymentReferenceNumber:
            (updatedData && updatedData.paymentReferenceNumber) || '',
        },
        taxRate,
      };

      await addDoc(
        collection(db, 'Businesses', bid, 'Ledger'),
        ledgerData
      );

      setReceivePaymentModal(false);
      setInvoiceData({ paymentMode: 'Cash' });
      getNewReceiptID();
    } catch (error) {
      console.log('Receipt [Error] : ', error);
    }
  }

  function handleReceivePayment(invData) {
    if (!invData.amountReceived) {
      setInputError({
        ...(inputError || {}),
        amountReceived: 'Amount field is required.',
      });
      toast.error('Please enter the amount!');
    } else if (!invData.paymentDate) {
      setInputError({
        ...(inputError || {}),
        paymentDate: 'This field is required.',
      });
    } else if (!invData.paymentMode) {
      setInputError({
        ...(inputError || {}),
        paymentMode: 'This field is required.',
      });
    } else if (
      (invData.paymentMode === 'Debit Card' ||
        invData.paymentMode === 'Credit Card' ||
        invData.paymentMode === 'Cheque') &&
      !invData.paymentReferenceNumber
    ) {
      setInputError({
        ...(inputError || {}),
        paymentReferenceNumber: 'This field is required.',
      });
    } else if (
      (invData.paymentMode === 'Bank' ||
        invData.paymentMode === 'UPI') &&
      !invData.paymentReceivedFrom
    ) {
      setInputError({
        ...(inputError || {}),
        paymentReceivedFrom: 'This field is required.',
      });
    } else {
      handleSaveReceipt(location.state.items.id, invData.id, invData);
    }
  }

  // ---------- TOTALS USING NEW MODEL ----------
  const SubTotal =
    safeNum(item?.basicAmount) + safeNum(item?.additionalCharges);
  const TaxableAmount = SubTotal - safeNum(item?.discountAmount);
  const TaxAmount = (TaxableAmount * safeNum(item?.taxRate)) / 100;

  const totals = getTotalsForInvoices(Array.isArray(invoices) ? invoices : []);

  const totalInvoiceBase =
    totals.totalPrincipal + totals.totalTax + totals.totalOverdue;

  const totalPaidAmount =
    totals.totalPrincipalReceived +
    totals.totalTaxReceived +
    totals.totalOverdueReceived;

  const totalOverdue = totals.totalOverdue;
  const totalOverdueReceived = totals.totalOverdueReceived;
  const totalOutstanding = totals.totalOutstanding;

  return (
    <section className='section'>
      <OutletHead
        left={
          <div className='d-flex align-items-center'>
            <Button
              variant='light'
              size='sm'
              className='p-2 me-3 rounded-circle'
              onClick={() => navigate(-1)}
            >
              <IoArrowBackOutline size={24} />
            </Button>
            <h5 className='mb-0 fw-600'>
              Invoices ({item && item.id})
            </h5>
          </div>
        }
        right={
          <div className='d-flex align-items-center justify-content-end'>
            <Button
              variant='outline-primary'
              type='button'
              className='py-2 px-4'
              size='sm'
            >
              <IoDocumentTextOutline size={18} />
              <span className='ps-2'>Export</span>
            </Button>
          </div>
        }
      />

      {isLoading ? (
        <div className='inner-wrapper bg-light border-top'>
          <div className='row px-3 pt-3'>
            <div className='col-md-4 col-12'>
              <Skeleton
                height={150}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
            <div className='col-md-4 col-12'>
              <Skeleton
                height={150}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
            <div className='col-md-4 col-12'>
              <Skeleton
                height={150}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
            <div className='col-12 mt-3'>
              <Skeleton
                height={80}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
            <div className='col-12 mt-3'>
              <Skeleton
                height={300}
                baseColor='var(--primary-light)'
                highlightColor='linear-gradient(to right, #f7f8ff ,rgb(239, 239, 255))'
              />
            </div>
          </div>
        </div>
      ) : (
        <div className='inner-wrapper bg-white border-top'>
          {/* TOP SUMMARY */}
          <div className='rounded border overflow-hidden mb-3 m-3'>
            <div className='row'>
              <div className='col-md-8'>
                <div className='row p-3 pe-md-0'>
                  <div className='col-md-6'>
                    <div className='mb-3'>
                      <p className='mb-2'>To</p>
                      <h5 className='mb-0'>
                        {item?.partyDetails?.partyName}
                      </h5>
                      {item?.partyDetails?.gst ? (
                        <p className='mb-0'>
                          <small>
                            GSTIN - {item.partyDetails.gst}
                          </small>
                        </p>
                      ) : null}
                      {item?.partyDetails?.address ? (
                        <p className='mb-0'>
                          <small>
                            {item.partyDetails.address},&nbsp;
                            {item.partyDetails.city},&nbsp;
                            {item.partyDetails.pincode},&nbsp;
                            {item.partyDetails.state}
                          </small>
                        </p>
                      ) : null}
                      {item?.partyDetails?.phoneNumber ? (
                        <p className='mb-0'>
                          <small>
                            +91 {item.partyDetails.phoneNumber}
                          </small>
                        </p>
                      ) : null}
                    </div>
                  </div>
                  <div className='col-md-6 text-end'>
                    <div className='mb-3'>
                      <p className='mb-2'>From</p>
                      <h5 className='mb-0'>
                        {businessProfile?.businessName}
                      </h5>
                      <p className='mb-0'>
                        <small>
                          GSTIN - {businessProfile?.gst}
                        </small>
                      </p>
                      <p className='mb-0'>
                        <small>
                          {businessProfile?.address},&nbsp;
                          {businessProfile?.city},&nbsp;
                          {businessProfile?.pincode},&nbsp;
                          {businessProfile?.state}
                        </small>
                      </p>
                      <p className='mb-0'>
                        <small>
                          +91 {businessProfile?.phoneNumber}
                        </small>
                      </p>
                      <p className='mb-0'>
                        <small>{businessProfile?.email}</small>
                      </p>
                    </div>
                  </div>
                </div>
              </div>

              {/* SUMMARY CARD */}
              <div className='col-md-4'>
                <div className='h-100 mb-3 bg-light d-flex flex-column justify-content-between p-3'>
                  <div className='mb-0'>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>Sub Total</p>
                      <p className='mb-0 text-muted'>
                        ₹
                        {safeNum(SubTotal)
                          .toFixed(2)
                          .toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>
                        Discount{' '}
                        <span>
                          ({item?.discountPercent || 0}%)
                        </span>
                      </p>
                      <p className='mb-0 text-muted'>
                        <IoRemove /> ₹
                        {safeNum(item?.discountAmount)
                          .toFixed(2)
                          .toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>Tax Amount</p>
                      <p className='mb-0 text-muted'>
                        <IoAdd size={16} /> ₹
                        {safeNum(TaxAmount)
                          .toFixed(2)
                          .toLocaleString('en-IN')}
                      </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>
                        Overdue Charges (All Invoices)
                      </p>
                      <p className='mb-0 text-muted text-danger'>
                        <IoAdd /> {fmt(totalOverdue)}
                      </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <h5 className='mb-0 fw-600'>Total Amount</h5>
                      <h5 className='mb-0 fw-600'>
                        {fmt(totalInvoiceBase)}
                      </h5>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>
                        Received Amount
                      </p>
                      <p className='mb-0 text-muted'>
                        <IoRemove /> {fmt(totalPaidAmount)}
                      </p>
                    </div>
                    <div className='d-flex align-items-center justify-content-between mb-1'>
                      <p className='mb-0 text-muted'>
                        Overdue Received
                      </p>
                      <p className='mb-0 text-muted'>
                        {fmt(totalOverdueReceived)}
                      </p>
                    </div>
                  </div>

                  <div className='border-top text-danger pt-3'>
                    <div className='d-flex align-items-center justify-content-between'>
                      <h5 className='mb-0 fw-600'>
                        Outstanding Amount
                      </h5>
                      <h5 className='mb-0 fw-600'>
                        {fmt(totalOutstanding)}
                      </h5>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* PROPERTY DETAILS */}
          <div className='rounded border overflow-hidden mb-3 m-3'>
            <div className='p-3 bg-primary-light'>
              <h5 className='mb-0 text-muted fw-600'>
                Property Details
              </h5>
            </div>
            <div className='d-flex align-items-center justify-content-between border-top p-3'>
              <div className='W-60 d-flex align-items-start'>
                <div className='bg-primary-light rounded p-2'>
                  <CgAlignBottom size={34} color='#D4D4D4' />
                </div>
                <div className='ms-3'>
                  <h5 className='mb-0'>
                    <small>
                      {item?.projectDetails?.propName}
                    </small>
                  </h5>
                  <p className='mb-0 text-secondary'>
                    <small>
                      {item?.projectDetails?.propLocality},{' '}
                      {item?.projectDetails?.propCity},{' '}
                      {item?.projectDetails?.propPincode},{' '}
                      {item?.projectDetails?.propState}
                    </small>
                  </p>
                </div>
              </div>
              <div className='w-40'>
                <div className='text-end mb-1'>
                  <Badge
                    text='dark'
                    bg='fade alert alert-info px-3 py-1 fw-600'
                    className='mb-0'
                  >
                    Tower -{' '}
                    <span className='fw-600 me-2'>
                      {
                        item?.projectDetails?.bookedUnit
                          ?.unitTower?.name
                      }
                    </span>
                  </Badge>
                </div>
                <p className='mb-0 text-end'>
                  <small>
                    <span className='pe-2'>
                      Floor -{' '}
                      <span className='fw-600'>
                        {
                          item?.projectDetails?.bookedUnit
                            ?.unitFloor
                        }
                      </span>
                    </span>
                    <span className='border-start ps-2'>
                      Unit No. -{' '}
                      <span className='fw-600'>
                        {
                          item?.projectDetails?.bookedUnit
                            ?.unitNumber
                        }
                      </span>
                    </span>
                  </small>
                </p>
              </div>
            </div>
          </div>

          {/* INVOICES TABLE */}
          <div className='rounded border overflow-hidden mb-3 m-3'>
            <div className='p-3 bg-primary-light'>
              <h5 className='mb-0 text-muted fw-600'>Invoices</h5>
            </div>

            <Table hover className='align-middle mb-0'>
              <thead className='border-bottom'>
                <tr className='text-center'>
                  <th className='fw-normal text-muted'>
                    <small>S.No.</small>
                  </th>
                  <th className='fw-normal text-muted text-start'>
                    <small>Particular</small>
                  </th>
                  <th className='fw-normal text-muted'>
                    <small>Due Date</small>
                  </th>
                  <th className='fw-normal text-muted text-end'>
                    <small>Principal</small>
                  </th>
                  <th className='fw-normal text-muted text-end'>
                    <small>Tax</small>
                  </th>
                  <th className='fw-normal text-muted text-end'>
                    <small>Overdue</small>
                  </th>
                  <th className='fw-normal text-muted'>
                    <small>Payment Date</small>
                  </th>
                  <th className='fw-normal text-muted'>
                    <small>Status</small>
                  </th>
                </tr>
              </thead>
              <tbody>
                {Array.isArray(invoices) &&
                  invoices.map((invItem, index) => {
                    const {
                      principal,
                      principalReceived,
                      tax,
                      taxReceived,
                      overdueInterest,
                      overdueInterestReceived,
                      overdueTax,
                      overdueTaxReceived,
                    } = getInvoiceAmounts(invItem);

                    const overdueTotal =
                      overdueInterest + overdueTax;
                    const overdueReceivedTotal =
                      overdueInterestReceived +
                      overdueTaxReceived;
                    const overdueOutstanding =
                      round2(
                        overdueTotal - overdueReceivedTotal
                      );

                    const totalInvAmount =
                      principal + tax + overdueTotal;
                    const totalInvReceived =
                      principalReceived +
                      taxReceived +
                      overdueReceivedTotal;
                    const totalOutstandingInv =
                      invoiceOutstanding(invItem);

                    const epsilon = 0.01;
                    let statusLabel = 'Due';
                    let statusVariant = 'secondary';

                    if (totalOutstandingInv <= epsilon) {
                      statusLabel = 'Paid';
                      statusVariant = 'success';
                    } else if (!invItem.isCompleted) {
                      statusLabel = 'Yet to start!';
                      statusVariant = 'secondary';
                    } else if (overdueOutstanding > epsilon) {
                      statusLabel = 'Overdue';
                      statusVariant = 'danger';
                    } else if (totalInvReceived <= epsilon) {
                      statusLabel = 'Due';
                      statusVariant = 'danger';
                    } else if (
                      totalInvReceived <
                      totalInvAmount - epsilon
                    ) {
                      statusLabel = 'Partially Paid';
                      statusVariant = 'primary';
                    }

                    const dueDateStr = invItem.dueDate
                      ? new Date(
                        invItem.dueDate.seconds * 1000
                      ).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                      })
                      : '-';

                    const paymentDateStr = invItem.paymentDate
                      ? new Date(
                        invItem.paymentDate.seconds * 1000
                      ).toLocaleString('en-US', {
                        month: 'short',
                        day: '2-digit',
                        year: 'numeric',
                      })
                      : '-';

                    return (
                      <tr
                        key={invItem.id || index}
                        className={
                          index === invoices.length - 1
                            ? 'text-center'
                            : 'text-center border-bottom'
                        }
                      >
                        <td>{index + 1}</td>

                        {!invItem.isCompleted ? (
                          <td className='text-start text-muted'>
                            <p className='mb-0'>
                              {invItem.name}
                            </p>
                            <p className='mb-0 text-muted'>
                              (
                              <small>
                                Invoice - {invItem.id}
                              </small>
                              )
                            </p>
                          </td>
                        ) : (
                          <td
                            className={
                              totalOutstandingInv <= epsilon
                                ? 'text-start text-success cursor-pointer'
                                : 'text-start text-primary cursor-pointer'
                            }
                            onClick={() =>
                              navigate(`${invItem.id}`, {
                                state: {
                                  bookingData: location.state.items,
                                  invoiceID: invItem.id,
                                },
                              })
                            }
                          >
                            <p className='mb-0'>
                              {invItem.name}
                            </p>
                            <p className='mb-0 text-muted'>
                              (
                              <small>
                                Invoice - {invItem.id}
                              </small>
                              )
                            </p>
                          </td>
                        )}

                        <td>{dueDateStr}</td>

                        <td className='text-end'>
                          {fmt(principal)}
                        </td>
                        <td className='text-end'>{fmt(tax)}</td>
                        <td className='text-end text-danger'>
                          {overdueTotal > 0 ? (
                            <>
                              <div>
                                {fmt(overdueOutstanding)}
                              </div>
                              <div className='small text-muted'>
                                <small>
                                  Rec:{' '}
                                  {fmt(
                                    overdueReceivedTotal
                                  )}
                                </small>
                              </div>
                            </>
                          ) : (
                            '-'
                          )}
                        </td>

                        <td>{paymentDateStr}</td>

                        <td>
                          {statusVariant === 'secondary' ? (
                            <Badge
                              text='secondary'
                              bg='fade alert alert-secondary px-3 py-1 fw-normal'
                              className='mb-0 fw-600 text-secondary'
                            >
                              {statusLabel}
                            </Badge>
                          ) : (
                            <Badge
                              bg={`${statusVariant} px-3 py-1 fw-normal`}
                              className='mb-0 fw-600'
                            >
                              {statusLabel}
                            </Badge>
                          )}
                        </td>
                      </tr>
                    );
                  })}
              </tbody>
            </Table>
          </div>
        </div>
      )}

      {/* LEGACY RECEIVE PAYMENT MODAL (single invoice) */}
      <Modal
        show={receivePaymentModal}
        onHide={() => {
          setReceivePaymentModal(false);
          setInputError(null);
        }}
        backdrop='static'
        keyboard={false}
        size='lg'
      >
        <Modal.Header closeButton>
          <Modal.Title>
            <small>Receive Payment</small>
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className='row'>
            <div className='col-md-6'>
              <Form.Group className='mb-3' controlId='paymentDate'>
                <Form.Label>Date of Payment *</Form.Label>
                <Form.Control
                  type='date'
                  onChange={(event) => {
                    setInvoiceData({
                      ...invoiceData,
                      paymentDate: event.target.value
                        ? new Date(event.target.value)
                        : '',
                    });
                    setInputError({
                      ...(inputError || {}),
                      paymentDate: '',
                    });
                  }}
                  className={
                    inputError && inputError.paymentDate
                      ? 'border-danger'
                      : ''
                  }
                  value={
                    invoiceData && invoiceData.paymentDate
                      ? format(
                        invoiceData.paymentDate,
                        'yyyy-MM-dd'
                      )
                      : ''
                  }
                  isInvalid={
                    inputError && inputError.paymentDate
                  }
                />
                <Form.Control.Feedback type='invalid'>
                  {inputError && inputError.paymentDate}
                </Form.Control.Feedback>
              </Form.Group>
              <Form.Group
                className='mb-4'
                controlId='paymentRemark'
              >
                <Form.Label>Remark (Optional)</Form.Label>
                <Form.Control
                  as='textarea'
                  rows={3}
                  placeholder='Enter payment remark'
                  onChange={(event) =>
                    setInvoiceData({
                      ...invoiceData,
                      paymentRemark: event.target.value,
                    })
                  }
                  value={
                    (invoiceData &&
                      invoiceData.paymentRemark) ||
                    ''
                  }
                />
              </Form.Group>
              <Form.Group
                className='mb-3'
                controlId='paymentMode'
              >
                <Form.Label>Mode of Payment *</Form.Label>
                <Form.Select
                  value={
                    (invoiceData && invoiceData.paymentMode) ||
                    ''
                  }
                  onChange={(event) => {
                    setInvoiceData({
                      ...invoiceData,
                      paymentMode: event.target.value,
                      paymentReferenceNumber: '',
                      paymentReceivedFrom: null,
                      chequeDate: null,
                    });
                    setInputError({
                      ...(inputError || {}),
                      paymentMode: '',
                      paymentReferenceNumber: '',
                      paymentReceivedFrom: '',
                    });
                  }}
                  className={
                    inputError && inputError.paymentMode
                      ? 'border-danger'
                      : ''
                  }
                  isInvalid={
                    inputError && inputError.paymentMode
                  }
                >
                  <option value=''>Select</option>
                  <option value='Cash'>Cash</option>
                  <option value='Debit Card'>Debit Card</option>
                  <option value='Credit Card'>Credit Card</option>
                  <option value='Bank'>Bank</option>
                  <option value='RTGS/NEFT'>RTGS/NEFT</option>
                  <option value='Cheque'>Cheque</option>
                  <option value='UPI'>UPI</option>
                </Form.Select>
                <Form.Control.Feedback type='invalid'>
                  {inputError && inputError.paymentMode}
                </Form.Control.Feedback>
              </Form.Group>
            </div>

            <div className='col-md-6'>
              <Form.Group
                className='mb-5'
                controlId='installmentAmount'
              >
                <Form.Label>Installment Amount *</Form.Label>
                <InputGroup>
                  <InputGroup.Text className='py-1'>
                    ₹
                  </InputGroup.Text>
                  <Form.Control
                    disabled={
                      !!(
                        invoiceData &&
                        invoiceData.installmentAmount
                      )
                    }
                    type='number'
                    className='border-left-0'
                    placeholder='0'
                    value={
                      invoiceData &&
                      invoiceData.installmentAmount
                    }
                  />
                </InputGroup>
              </Form.Group>

              <div
                className={
                  inputError && inputError.amountReceived
                    ? 'border border-danger rounded px-3'
                    : 'border rounded px-3'
                }
              >
                <Form.Group
                  className='mb-3'
                  controlId='balanceAmount'
                >
                  <div className='d-flex align-items-center justify-content-between text-success pt-3'>
                    <div>Paid Amount</div>
                    <div className='pe-2'>
                      {(
                        safeNum(
                          invoiceData && invoiceData.paidAmount
                        ) || 0
                      ).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'INR',
                      })}
                    </div>
                  </div>
                </Form.Group>
                <div className='d-flex align-items-center justify-content-between py-3 mb-3'>
                  <div className='w-40 h5 mb-0'>Amount</div>
                  <Form.Group controlId='amountReceived'>
                    <Form.Control
                      type='number'
                      placeholder='0'
                      onFocus={(e) => e.target.select()}
                      className={
                        inputError && inputError.amountReceived
                          ? 'border-danger text-end'
                          : 'text-end'
                      }
                      onChange={(event) => {
                        const val = safeNum(
                          event.target.value
                        );
                        const instAmt = safeNum(
                          invoiceData &&
                          invoiceData
                            .installmentAmount
                        );
                        if (val > instAmt) {
                          setInputError({
                            ...(inputError || {}),
                            amountReceived:
                              'Amount must be less than or equal to Installment.',
                          });
                        } else {
                          setInvoiceData({
                            ...invoiceData,
                            amountReceived:
                              event.target.value,
                          });
                          setInputError({
                            ...(inputError || {}),
                            amountReceived: '',
                          });
                        }
                      }}
                      value={
                        invoiceData &&
                          invoiceData.amountReceived
                          ? invoiceData.amountReceived
                          : ''
                      }
                    />
                  </Form.Group>
                </div>
                {inputError && inputError.amountReceived ? (
                  <Alert
                    variant='danger'
                    className='py-0 text-center'
                  >
                    <small>
                      {inputError && inputError.amountReceived}
                    </small>
                  </Alert>
                ) : null}
                <Form.Group
                  className='mb-3'
                  controlId='balanceAmount'
                >
                  <div className='d-flex align-items-center justify-content-between text-danger'>
                    <div>Balance Amount</div>
                    <div className='pe-2'>
                      {(
                        safeNum(
                          invoiceData &&
                          invoiceData.installmentAmount
                        ) -
                        (safeNum(
                          invoiceData && invoiceData.paidAmount
                        ) +
                          safeNum(
                            invoiceData &&
                            invoiceData.amountReceived
                          ))
                      ).toLocaleString('en-US', {
                        style: 'currency',
                        currency: 'INR',
                      })}
                    </div>
                  </div>
                </Form.Group>
              </div>
            </div>

            {/* Reference / Bank / UPI blocks remain same as your original code */}
            {(invoiceData &&
              invoiceData.paymentMode &&
              (invoiceData.paymentMode === 'Cheque' ||
                invoiceData.paymentMode === 'Debit Card' ||
                invoiceData.paymentMode === 'Credit Card' ||
                invoiceData.paymentMode === 'RTGS/NEFT')) && (
                <div
                  className={
                    invoiceData.paymentMode === 'Cheque'
                      ? 'col-md-6'
                      : 'col-md-12'
                  }
                >
                  <Form.Group
                    className='mb-3'
                    controlId='paymentReferenceNumber'
                  >
                    <Form.Label>
                      UTR No. (Unique Transaction Reference
                      Number)
                    </Form.Label>
                    <Form.Control
                      placeholder='Enter UTR/Reference No.'
                      onChange={(event) => {
                        setInvoiceData({
                          ...invoiceData,
                          paymentReferenceNumber:
                            event.target.value,
                        });
                        setInputError({
                          ...(inputError || {}),
                          paymentReferenceNumber: '',
                        });
                      }}
                      value={
                        (invoiceData &&
                          invoiceData.paymentReferenceNumber) ||
                        ''
                      }
                      className={
                        inputError &&
                          inputError.paymentReferenceNumber
                          ? 'border-danger'
                          : ''
                      }
                      isInvalid={
                        inputError &&
                        inputError.paymentReferenceNumber
                      }
                    />
                    <Form.Control.Feedback type='invalid'>
                      {inputError &&
                        inputError.paymentReferenceNumber}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}

            {invoiceData &&
              invoiceData.paymentMode === 'Cheque' && (
                <div className='col-md-6'>
                  <Form.Group
                    className='mb-3'
                    controlId='chequeDate'
                  >
                    <Form.Label>Cheque Date</Form.Label>
                    <Form.Control
                      type='date'
                      onChange={(event) =>
                        setInvoiceData({
                          ...invoiceData,
                          chequeDate: new Date(
                            event.target.value
                          ),
                        })
                      }
                      className='border-right-0'
                      value={
                        invoiceData &&
                          invoiceData.chequeDate
                          ? format(
                            invoiceData.chequeDate,
                            'yyyy-MM-dd'
                          )
                          : ''
                      }
                    />
                  </Form.Group>
                </div>
              )}

            {invoiceData &&
              invoiceData.paymentMode === 'Bank' && (
                <div className='col-md-12'>
                  <Form.Group
                    className='mb-3'
                    controlId='paymentReceivedFrom'
                  >
                    <Form.Label>
                      Payment Received from *
                    </Form.Label>
                    <Form.Select
                      value={
                        invoiceData &&
                          invoiceData.paymentReceivedFrom
                          ? JSON.stringify(
                            invoiceData.paymentReceivedFrom
                          )
                          : ''
                      }
                      onChange={(event) => {
                        setInvoiceData({
                          ...invoiceData,
                          paymentReceivedFrom: event.target.value
                            ? JSON.parse(event.target.value)
                            : '',
                        });
                        setInputError({
                          ...(inputError || {}),
                          paymentReceivedFrom: '',
                        });
                      }}
                      className={
                        inputError &&
                          inputError.paymentReceivedFrom
                          ? 'border-danger'
                          : ''
                      }
                      isInvalid={
                        inputError &&
                        inputError.paymentReceivedFrom
                      }
                    >
                      <option value=''>Select</option>
                      {item?.partyDetails?.bankDetails?.map(
                        (bk_item, bkIndex) => (
                          <option
                            key={bkIndex}
                            value={JSON.stringify(bk_item)}
                          >
                            {bk_item.accountHolderName} -{' '}
                            {bk_item.accountNumber}
                          </option>
                        )
                      )}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {inputError &&
                        inputError.paymentReceivedFrom}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}

            {invoiceData &&
              invoiceData.paymentMode === 'UPI' && (
                <div className='col-md-12'>
                  <Form.Group
                    className='mb-3'
                    controlId='paymentReceivedFrom'
                  >
                    <Form.Label>
                      Payment Received from *
                    </Form.Label>
                    <Form.Select
                      value={
                        invoiceData &&
                          invoiceData.paymentReceivedFrom
                          ? JSON.stringify(
                            invoiceData.paymentReceivedFrom
                          )
                          : ''
                      }
                      onChange={(event) => {
                        setInvoiceData({
                          ...invoiceData,
                          paymentReceivedFrom: event.target.value
                            ? JSON.parse(event.target.value)
                            : '',
                        });
                        setInputError({
                          ...(inputError || {}),
                          paymentReceivedFrom: '',
                        });
                      }}
                      className={
                        inputError &&
                          inputError.paymentReceivedFrom
                          ? 'border-danger'
                          : ''
                      }
                      isInvalid={
                        inputError &&
                        inputError.paymentReceivedFrom
                      }
                    >
                      <option value=''>Select</option>
                      {item?.partyDetails?.bankDetails?.map(
                        (bk_item, bkIndex) => (
                          <option
                            key={bkIndex}
                            value={JSON.stringify(
                              bk_item.upi
                            )}
                          >
                            {bk_item.upi}
                          </option>
                        )
                      )}
                    </Form.Select>
                    <Form.Control.Feedback type='invalid'>
                      {inputError &&
                        inputError.paymentReceivedFrom}
                    </Form.Control.Feedback>
                  </Form.Group>
                </div>
              )}
          </div>
        </Modal.Body>
        <Modal.Footer>
          <Button
            variant='outline-danger'
            onClick={() => setReceivePaymentModal(false)}
            className='px-5 py-2'
          >
            Cancel
          </Button>
          <Button
            disabled={inputError && inputError.amountReceived}
            variant='primary'
            className='px-5 py-2'
            onClick={() => handleReceivePayment(invoiceData)}
          >
            Receive Payment
          </Button>
        </Modal.Footer>
      </Modal>
    </section>
  );
};

export default Invoices;